import { OpForm } from 'new-components/DLS/OpForm/OpForm';
import { useTranslation } from 'react-i18next';
import { ComponentProps, useState } from 'react';
import clsx from 'clsx';
import { OpTextButton } from 'new-components/DLS/OpTextButton/OpTextButton';
import { getWindowLocation } from 'utils/window';
import { useDispatch } from 'react-redux';
import { setAlert } from 'routes/AppContainer/actions';
import { useAltaLoginUrlParams } from '../../hooks/useAltaLoginUrlParams';
import { getCloudServerRegions } from '../../helpers/getCloudServerRegions';
import { ErrorAlert } from '../ErrorAlert/ErrorAlert';

import './ResetPasswordFlow.scss';

interface ResetPasswordFlowProps {
  backToSignin(email: string): void;
}

/**
 * @component
 * @description Component that allows the user to choose a new password if a valid token is the search params
 * and gets redirected to signin page with the correct url search params when successful
 */
export const ResetPasswordFlow = ({ backToSignin }: ResetPasswordFlowProps) => {
  const { t } = useTranslation();
  const { token, emailParam, region, orgId, service } = useAltaLoginUrlParams();
  const [error, setError] = useState<string>();
  const dispatch = useDispatch();

  const handleBackToSignIn = () => {
    const params = new URLSearchParams({
      ...(region && { region }),
      ...(emailParam && { email: emailParam }),
      ...(orgId && { orgId }),
      ...(service && { service }),
    });

    dispatch(
      setAlert('success', t('Your password has been successfully set.')),
    );

    const windowLocation = getWindowLocation();

    window.history.replaceState(
      null,
      '',
      `${windowLocation.origin}${windowLocation.pathname}?${params}`,
    );

    backToSignin(params.get('email')!);
  };

  const handleSubmit: ComponentProps<typeof OpForm>['onSubmit'] = async ({
    values,
  }) => {
    const availableRegions = await getCloudServerRegions();
    const currentRegion = availableRegions.data.find(
      (reg) => reg.env === region,
    );

    const heliumEndpoint = currentRegion?.helium?.endpoint;

    if (!heliumEndpoint) {
      setError(t('Missing required information'));
      return;
    }

    const changePasswordResponse = await fetch(
      `${heliumEndpoint}/tokens/resetPasswordTokens/${token}/use`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: values.password,
        }),
      },
    );

    if (
      changePasswordResponse.status >= 200 &&
      changePasswordResponse.status < 300
    ) {
      handleBackToSignIn();
    } else {
      const errorData = await changePasswordResponse.json();
      setError(errorData.message);
    }
  };

  return (
    <div className="reset-password">
      {error && <ErrorAlert message={error} />}
      <OpForm
        hasError={false}
        className={clsx({
          'reset-password__form': !error,
          'reset-password__form-error': error,
        })}
        onSubmit={handleSubmit}
        defaultButtons={false}
      >
        <OpForm.PasswordInput
          enforcePolicy
          name="password"
          className="reset-password__input"
          label={t('New password')}
          validateTrigger="onSubmit"
          rules={[
            {
              required: true,
              message: t('Password is required')!,
            },
          ]}
        />
        <OpForm.Button
          className="reset-password__submit"
          type="primary"
          htmlType="submit"
        >
          {t('Set password')}
        </OpForm.Button>
      </OpForm>
      <OpTextButton
        className="reset-password__back-button"
        onClick={handleBackToSignIn}
      >
        {t('Back to sign in')}
      </OpTextButton>
    </div>
  );
};
